import React, {useEffect, useRef, useState} from "react";
import {connect} from "react-redux";
import {withTranslation} from "react-i18next";
import {withRouter} from "react-router";
import classNames from "classnames";
import "./index.scss";

import {currentUserIsDepotRole, currentUserIsSupervisorRole, getFullName} from "../../../services/userModel";
import {getUserConnectionData} from "../../../utils/auth-util";
import {getConvertedCloudUrl} from "../../../utils/image-util";
import {createAction} from "../../../utils/sagaHelper";

import {LOGOUT} from "../../../store/reducers/authentication/authentication-actions";
import {DEPOT_SQUARE_ASSOCIATED_LOCATION_CHECK} from "../../../store/reducers/userModel/userModel-actions";
import {RECONCILIATIONS_MODEL} from "../../../utils/enums.js";
import {getRestaurantPaymentEnabled} from "../../../services/restaurant.js";

const UserControlsMenu = ({
  t,
  users,
  restaurant,
  history,
  logout,
  getDepotSquareAssociatedLocation,
  showItemsInPlace,
  // Mobile panel renders the component twice to sandwich the nav menu between the
  // account header (headerOnly) and the control items (itemsOnly).
  headerOnly,
  itemsOnly,
  // Called after any item click so the mobile panel can close over the opened page.
  onNavigate,
}) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const ref = useRef();
  const userConnectionData = getUserConnectionData();
  const userId = userConnectionData.unique_name;
  const user = users && users.find(user => user.userId === userId);
  const userName =
    getFullName(user) || (user && user.userName) || userConnectionData.sub;
  const avatar = getConvertedCloudUrl(user && user.avatarUrl);

  const getAccountName = () => {
    let name = (restaurant && restaurant.friendlyName) || "";
    const activeCenter =
      restaurant &&
      restaurant.activeControlCenter &&
      restaurant.controlCenters &&
      restaurant.controlCenters.find(
        controlCenter =>
          controlCenter.controlCenterId ===
          restaurant.activeControlCenter.controlCenterId
      );
    if (activeCenter) name += " " + (activeCenter.name || "");
    return name;
  };

  const isShowBillingItem = () => {
    return currentUserIsSupervisorRole() && !!(restaurant && restaurant.hasBillingProfile);
  };

  const handleOnLogout = () => {
    logout();
    history.push("/login");
    onNavigate && onNavigate();
  };

  const handleOnBilling = () => {
    history.push("/billing");
    onNavigate && onNavigate();
  };
  const handleOnSmsPackages = () => {
    history.push("/billing/smspackages");
    onNavigate && onNavigate();
  };
  const handleOnReconciliations = () => {
    history.push(RECONCILIATIONS_MODEL.configuration);
    onNavigate && onNavigate();
  };
  const handleOnReport = () => {
    history.push("/reports-history");
    onNavigate && onNavigate();
  }

  const handleOnDepotSquareIntegration = () => {
    getDepotSquareAssociatedLocation();
    onNavigate && onNavigate();
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target))
        setMenuOpen(false);
    }

    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  });

  // Line icons stroked with currentColor, so the row's hover color tints the icon
  // along with the text — the legacy PNG sprites stayed one fixed shade.
  // Not class "icon": AssociateOrderPopup's stylesheet declares .icon GLOBALLY with a
  // sprite background, which would paint a gray blob behind these.
  const MenuIcon = ({ children }) => (
      <svg className={"menu-icon"} viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
      </svg>
  );

  const Items = () => {
    return <ul>
      {currentUserIsDepotRole() && (
          <li onClick={handleOnDepotSquareIntegration}>
            <div className={classNames("icon", "square")} />
            <div>{t("settings.square.ICON_TOOLTIP")}</div>
          </li>
      )}
      {isShowBillingItem() && (
          <li onClick={handleOnBilling}>
            <MenuIcon>
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" />
            </MenuIcon>
            <div>{t("header.menu.BILLING_LABEL")}</div>
          </li>
      )}
      {isShowBillingItem() && (
          <li onClick={handleOnSmsPackages}>
            <MenuIcon>
              <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </MenuIcon>
            <div>{t("header.menu.SMS_PACKAGES_LABEL")}</div>
          </li>
      )}
      {(currentUserIsSupervisorRole() && getRestaurantPaymentEnabled()) && (
          <li onClick={handleOnReconciliations}>
            <MenuIcon>
              <rect x="5" y="3" width="14" height="18" rx="2" />
              <path d="M9 13l2 2 4-4" />
            </MenuIcon>
            <div>{t("header.menu.RECONCILIATIONS_LABEL")}</div>
          </li>
      )}
      <li onClick={handleOnReport}>
        <MenuIcon>
          <path d="M4 20h16" />
          <path d="M7 20v-5M12 20V7M17 20v-9" />
        </MenuIcon>
        <div>{t("report-download.REPORTS")}</div>
      </li>
      <li onClick={handleOnLogout}>
        <MenuIcon>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="M16 17l5-5-5-5" />
          <path d="M21 12H9" />
        </MenuIcon>
        <div>{t("header.user.LOG_OUT_LABEL")}</div>
      </li>
    </ul>
  }

  return (
      <div ref={ref} className={classNames("userControlsMenu")}
           onClick={() => setMenuOpen(!isMenuOpen)}
      >
        {!itemsOnly && (
            <div className={'userContents'}>
              <div>
                {avatar && (
                    <img src={avatar} alt="avatar" className={classNames("avatar")} />
                )}
              </div>
              <div className={classNames("info")}>
                <div
                    className={classNames("datauser", "restaurant")}
                    title={getAccountName()}
                >
                  {getAccountName()}
                </div>
                <div className={classNames("datauser", "userName")} title={userName}>
                  <div>{userName}</div>
                  <div className={classNames("menuArrow", { open: isMenuOpen })} />
                </div>
              </div>
            </div>
        )}
        {/*showItemsInPlace*/}
        {isMenuOpen && !showItemsInPlace && <Items/>}
        {showItemsInPlace && !headerOnly && <Items/>}
      </div>
  );
};

const mapStateToProps = state => ({
  users: state.userModel.data || [],
  restaurant: state.restaurant.data,
  menuHeaderPriority: state.menu.menuHeaderPriority
});

const mapDispatchToProps = dispatch => {
  return {
    logout: () => dispatch({ type: LOGOUT }),
    getDepotSquareAssociatedLocation: data =>
      dispatch(createAction(DEPOT_SQUARE_ASSOCIATED_LOCATION_CHECK, data))
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(UserControlsMenu))
);
